import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Menu, X, ChevronDown, User, Server, Globe, Shield, Gamepad2, Building2, Database, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useSiteContent } from '../contexts/SiteContentContext';
import { getLogoSrc } from '../constants/brandAssets';
import { scrollToTop } from '../utils/scrollToTop';

const Header = React.memo(() => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const { theme, toggleTheme, isDark } = useTheme();
  const { siteSettings } = useSiteSettings();
  const { getContent } = useSiteContent();
  const navigate = useNavigate();
  const location = useLocation();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  const menuItems = useMemo(() => [
    {
      name: getContent('navbar.item.home', 'Anasayfa'),
      path: '/',
      hasSubmenu: false,
      icon: Globe
    },
    {
      name: getContent('navbar.item.hosting', 'Hosting'),
      path: '/paketler', // Hosting butonuna basınca /paketler'e yönlendir
      hasSubmenu: true,
      icon: Database,
      submenu: [
        {
          name: getContent('navbar.hosting.web.name', 'Web Hosting'),
          path: '/web-hosting',
          description: getContent('navbar.hosting.web.desc', 'Hızlı ve güvenilir web hosting'),
          icon: '/images/hosting-icons/webhosting.webp'
        },
        {
          name: getContent('navbar.hosting.wordpress.name', 'WordPress Hosting'),
          path: '/wordpress-hosting',
          description: getContent('navbar.hosting.wordpress.desc', 'Optimize edilmiş WordPress hosting'),
          icon: '/images/hosting-icons/wordpress.webp'
        },
        {
          name: getContent('navbar.hosting.reseller.name', 'Reseller Hosting'),
          path: '/reseller-hosting',
          description: getContent('navbar.hosting.reseller.desc', 'Hosting satışı yapmak için'),
          icon: '/images/hosting-icons/reseller.webp'
        }
      ]
    },
    {
      name: getContent('navbar.item.server', 'Sunucu'),
      path: null, // Sunucu butonuna basınca yönlendirme
      hasSubmenu: true,
      icon: Server,
      submenu: [
        {
          name: getContent('navbar.server.intel.name', 'Intel'),
          path: '/intel-sunucular',
          description: getContent('navbar.server.intel.desc', 'Ekonomik Intel işlemcilerle her işin üstesinden gelin!'),
          icon: '/images/server-icons/intel.webp'
        },
        {
          name: getContent('navbar.server.ryzen5950.name', 'Ryzen 9 5950x'),
          path: '/ryzen-5950x',
          description: getContent('navbar.server.ryzen5950.desc', 'Yüksek Performanslı Ryzen İşlemcilerle Projelerinize Hız Katın!'),
          icon: '/images/server-icons/ryzen-5950x.webp'
        },
        {
          name: getContent('navbar.server.ryzen7950.name', 'Ryzen 9 7950x'),
          path: '/ryzen-7950x',
          description: getContent('navbar.server.ryzen7950.desc', 'Ultra Performanslı Ryzen İşlemcilerimizle her konuda öne geçin!'),
          icon: '/images/server-icons/ryzen-7950x.webp'
        },
        {
          name: getContent('navbar.server.nested.name', 'Nested Sunucu'),
          path: '/nested-sunucu',
          description: getContent('navbar.server.nested.desc', 'Sunucu hizmetlerimiz hakkında daha fazla bilgi edinin'),
          icon: '/images/server-icons/nested.webp'
        },
        {
          name: getContent('navbar.server.physical.name', 'Fiziksel Sunucu'),
          path: '/fiziksel-sunucu',
          description: getContent('navbar.server.physical.desc', 'Sunucu hizmetlerimiz hakkında daha fazla bilgi edinin'),
          icon: '/images/server-icons/physical.webp'
        },
        {
          name: getContent('navbar.server.colocation.name', 'Sunucu Barındırma'),
          path: '/sunucu-barindirma',
          description: getContent('navbar.server.colocation.desc', 'Kendi sunucunuzu profesyonel veri merkezimizde barındırın'),
          icon: '/images/server-icons/hosting.webp'
        }
      ]
    },
    {
      name: getContent('navbar.item.games', 'Oyun Sunucuları'),
      path: '/oyunlar',
      hasSubmenu: true,
      icon: Gamepad2,
      submenu: [
        {
          name: getContent('navbar.games.minecraft.name', 'Minecraft'),
          path: '/minecraft',
          description: getContent('navbar.games.minecraft.desc', 'Minecraft sunucu paketleri'),
          price: getContent('navbar.games.minecraft.price', '29₺\'den başlayan fiyatlarla'),
          icon: '/images/game-icons/minecraft.webp'
        },
        {
          name: getContent('navbar.games.cs2.name', 'Counter Strike 2'),
          path: '/cs2',
          description: getContent('navbar.games.cs2.desc', 'CS2 sunucu paketleri'),
          price: getContent('navbar.games.cs2.price', '49₺\'den başlayan fiyatlarla'),
          icon: '/images/game-icons/cs2.webp'
        },
        {
          name: getContent('navbar.games.rust.name', 'Rust'),
          path: '/rust',
          description: getContent('navbar.games.rust.desc', 'Hayatta kalma ve savaş'),
          price: getContent('navbar.games.rust.price', '79₺\'den başlayan fiyatlarla'),
          icon: '/images/game-icons/rust.webp'
        },
        {
          name: getContent('navbar.games.unturned.name', 'Unturned'),
          path: '/unturned',
          description: getContent('navbar.games.unturned.desc', 'Zombi hayatta kalma oyunu'),
          price: getContent('navbar.games.unturned.price', '29₺\'den başlayan fiyatlarla'),
          icon: '/images/game-icons/unturned.webp'
        },
        {
          name: getContent('navbar.games.scp.name', 'SCP'),
          path: '/scp',
          description: getContent('navbar.games.scp.desc', 'SCP sunucu paketleri'),
          price: getContent('navbar.games.scp.price', '39₺\'den başlayan fiyatlarla'),
          icon: '/images/game-icons/scp.webp'
        },
        {
          name: getContent('navbar.games.fivem.name', 'FiveM'),
          path: '/fivem',
          description: getContent('navbar.games.fivem.desc', 'GTA V roleplay sunucuları'),
          price: getContent('navbar.games.fivem.price', '59₺\'den başlayan fiyatlarla'),
          icon: '/images/game-icons/fivem.webp'
        }
      ]
    },
    {
      name: getContent('navbar.item.blog', 'Blog'),
      path: '/blog',
      hasSubmenu: false,
      icon: Building2
    },
    {
      name: getContent('navbar.item.corporate', 'Kurumsal'),
      path: '/kurumsal',
      hasSubmenu: true,
      icon: Shield,
      submenu: [
        {
          name: getContent('navbar.corporate.contact.name', 'İletişim'),
          path: '/iletisim',
          description: getContent('navbar.corporate.contact.desc', 'Bizimle iletişime geçin'),
          icon: '/images/corporate-icons/iletisim.webp'
        },
        {
          name: getContent('navbar.corporate.about.name', 'Hakkımızda'),
          path: '/hakkimizda',
          description: getContent('navbar.corporate.about.desc', 'Şirketimiz hakkında bilgi'),
          icon: '/images/corporate-icons/hakkimizda.webp'
        },
        {
          name: getContent('navbar.corporate.privacy.name', 'Gizlilik Politikası'),
          path: '/gizlilik-politikasi',
          description: getContent('navbar.corporate.privacy.desc', 'Gizlilik politikası'),
          icon: '/images/corporate-icons/gizlilik-sozlesmesi.webp'
        },
        {
          name: getContent('navbar.corporate.tos.name', 'Hizmet Sözleşmesi'),
          path: '/hizmet-sozlesmesi',
          description: getContent('navbar.corporate.tos.desc', 'Hizmet şartları ve koşulları'),
          icon: '/images/corporate-icons/gizlilik-sozlesmesi.webp'
        },
        {
          name: getContent('navbar.corporate.kvkk.name', 'KVKK Metni'),
          path: '/kvkk-metni',
          description: getContent('navbar.corporate.kvkk.desc', 'KVKK aydınlatma metni'),
          icon: '/images/corporate-icons/gizlilik-sozlesmesi.webp'
        },
        {
          name: getContent('navbar.corporate.acikriza.name', 'Açık Rıza Metni'),
          path: '/acik-riza-metni',
          description: getContent('navbar.corporate.acikriza.desc', 'Açık rıza metni'),
          icon: '/images/corporate-icons/gizlilik-sozlesmesi.webp'
        }
      ]
    }
  ], [getContent]);

  const handleMouseEnter = useCallback((index) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }

    if (menuItems[index].hasSubmenu) {
      setActiveDropdown(index);
    }
  }, [hoverTimeout, menuItems]);

  const handleMouseLeave = useCallback(() => {
    // Only close if we're not hovering over the dropdown
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 500); // Increased to 500ms for better UX

    setHoverTimeout(timeout);
  }, []);

  const handleDropdownMouseEnter = useCallback(() => {
    // Clear timeout when mouse enters dropdown area
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }, [hoverTimeout]);

  const handleDropdownMouseLeave = useCallback(() => {
    // Add a small delay before closing when leaving dropdown
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 200); // 200ms delay

    setHoverTimeout(timeout);
  }, []);

  return (
    <header className={`sticky top-0 z-50 ${isDark ? 'bg-slate-900/95 border-slate-800' : 'bg-gray-100/95 border-gray-300'} backdrop-blur-sm border-b`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-12 h-12 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 overflow-hidden relative">
              <img
                src={getLogoSrc(siteSettings)}
                alt="Logo"
                width="48"
                height="48"
                className="w-full h-full object-contain"
                onError={(e) => {
                  console.error('Logo yüklenemedi:', e);
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <div>
              <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {siteSettings.site_title || 'Keyzuvi Hosting'}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav
            className="hidden lg:flex items-center gap-1 relative"
            onMouseLeave={() => {
              // Only close if we're not hovering over the dropdown
              if (activeDropdown !== null) {
                const timeout = setTimeout(() => {
                  setActiveDropdown(null);
                }, 500);
                setHoverTimeout(timeout);
              }
            }}
          >
            {menuItems.map((item, index) => (
              <div
                key={index}
                className="relative"
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={() => {
                  // Don't close immediately, let the nav handle it
                }}
              >
                {item.path ? (
                  <Button
                    asChild
                    variant="ghost"
                    className={`h-12 px-6 ${isDark ? 'text-gray-300 hover:text-white hover:bg-slate-800' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} transition-all duration-300 flex items-center gap-2 ${location.pathname === item.path ? (isDark ? 'text-white bg-slate-800' : 'text-gray-900 bg-gray-100') : ''
                      } ${activeDropdown === index ? (isDark ? 'text-white bg-slate-800' : 'text-gray-900 bg-gray-100') : ''
                      } ${activeDropdown === index ? 'active-category' : ''}`}
                  >
                    <Link
                      to={item.path}
                      onClick={() => {
                        scrollToTop();
                        setActiveDropdown(null);
                      }}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                      {item.badge && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                          {item.badge}
                        </span>
                      )}
                      {item.hasSubmenu && <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === index ? 'rotate-180' : ''}`} />}
                    </Link>
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    className={`${isDark ? 'text-gray-300 hover:text-white hover:bg-slate-800' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} transition-all duration-300 flex items-center gap-2 ${activeDropdown === index ? (isDark ? 'text-white bg-slate-800' : 'text-gray-900 bg-gray-100') : ''
                      } ${activeDropdown === index ? 'active-category' : ''}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.name}
                    {item.badge && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                        {item.badge}
                      </span>
                    )}
                    {item.hasSubmenu && <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === index ? 'rotate-180' : ''}`} />}
                  </Button>
                )}
              </div>
            ))}

            {/* Fixed Dropdown Area */}
            {activeDropdown !== null && menuItems[activeDropdown].hasSubmenu && (
              <div
                className={`absolute top-full left-0 right-0 mt-2 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-100 border-gray-300'} rounded-lg shadow-xl border py-6 z-50 w-full max-w-6xl mx-auto`}
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleDropdownMouseLeave}
                style={{ pointerEvents: 'auto' }}
              >
                <div className="px-6 pb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${isDark ? 'bg-blue-900' : 'bg-blue-100'} flex items-center justify-center`}>
                      {React.createElement(menuItems[activeDropdown].icon, { className: `w-4 h-4 ${isDark ? 'text-blue-300' : 'text-blue-600'}` })}
                    </div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} text-xl`}>{menuItems[activeDropdown].name}</h3>
                    <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-green-400' : 'bg-green-600'} animate-pulse`}></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 px-6">
                  {menuItems[activeDropdown].submenu.map((subItem, subIndex) => {
                    const content = (
                      <div className="flex items-start gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${subItem.color ? `server-icon-${subItem.color}` : (isDark ? 'bg-slate-700' : 'bg-gray-100')
                          }`}>
                          {subItem.icon && subItem.icon.startsWith('/images/') ? (
                            <img
                              src={subItem.icon}
                              alt={subItem.name}
                              className="w-8 h-8 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          ) : subItem.icon ? (
                            <span className="text-2xl">{subItem.icon}</span>
                          ) : (
                            React.createElement(menuItems[activeDropdown].icon, { className: `w-5 h-5 ${isDark ? 'text-blue-300' : 'text-blue-600'}` })
                          )}
                          {subItem.icon && subItem.icon.startsWith('/images/') && (
                            <div className="w-5 h-5 hidden">
                              {React.createElement(menuItems[activeDropdown].icon, { className: `w-5 h-5 ${isDark ? 'text-blue-300' : 'text-blue-600'}` })}
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} text-sm`}>{subItem.name}</h4>
                            {subItem.badge && (
                              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                                {subItem.badge}
                              </span>
                            )}
                          </div>
                          <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-500'} leading-relaxed mb-1`}>{subItem.description}</p>
                          {subItem.price && (
                            <p className={`text-xs font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>{subItem.price}</p>
                          )}
                        </div>
                      </div>
                    );

                    return subItem.external ? (
                      <a
                        key={subIndex}
                        href={subItem.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          setActiveDropdown(null);
                        }}
                        className={`block p-4 ${isDark ? 'hover:bg-slate-700 border-slate-600 hover:border-slate-500' : 'hover:bg-gray-200 border-gray-200 hover:border-gray-300'} transition-colors duration-200 rounded-lg border hover:shadow-md`}
                      >
                        {content}
                      </a>
                    ) : (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        onClick={() => {
                          scrollToTop();
                          setActiveDropdown(null);
                        }}
                        className={`block p-4 ${isDark ? 'hover:bg-slate-700 border-slate-600 hover:border-slate-500' : 'hover:bg-gray-200 border-gray-200 hover:border-gray-300'} transition-colors duration-200 rounded-lg border hover:shadow-md`}
                      >
                        {content}
                      </Link>
                    );
                  })}
                </div>
                {/* Add "Tümünü Gör" button only for Oyun Sunucuları */}
                {activeDropdown !== null && menuItems[activeDropdown].name === 'Oyun Sunucuları' && (
                  <div className={`border-t ${isDark ? 'border-slate-700' : 'border-gray-200'} mt-6 pt-4 px-6`}>
                    <Link
                      to="/oyunlar"
                      aria-label="Tüm oyun sunucularını gör"
                      className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 h-11 ${isDark
                        ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 text-blue-300 hover:text-white hover:from-blue-600/30 hover:to-purple-600/30 hover:border-blue-400/50 hover:shadow-lg hover:shadow-blue-500/20'
                        : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 text-blue-600 hover:text-blue-700 hover:from-blue-100 hover:to-purple-100 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-200/50'
                        }`}
                    >
                      {getContent('navbar.games.all.name', 'Tümünü Gör')}
                      <div className="w-4 h-4 flex items-center justify-center">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Theme Toggle Button */}
            <Button
              variant="ghost"
              onClick={toggleTheme}
              aria-label={isDark ? "Açık temaya geç" : "Koyu temaya geç"}
              className={`w-12 h-12 ${isDark ? 'text-gray-300 hover:text-white hover:bg-slate-800' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} transition-all duration-300`}
            >
              {isDark ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
            </Button>

            {isLoggedIn ? (
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate('/kedihost')}
                  variant="outline"
                  className={`h-11 ${isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                >
                  Admin Panel
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6"
                >
                  <User className="w-5 h-5 mr-2" />
                  Hesabım
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => window.location.href = 'https://musteri.atahost.com.tr/hesap-olustur'}
                className="h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6"
              >
                <User className="w-5 h-5 mr-2" />
                Müşteri Paneli
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Mobil menüyü aç/kapat"
            className={`lg:hidden ${isDark ? 'text-white' : 'text-gray-700'} p-3 w-12 h-12 flex items-center justify-center`}
          >
            {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className={`lg:hidden py-4 border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
            <nav className="flex flex-col gap-2">
              {/* Theme Toggle for Mobile */}
              <Button
                variant="ghost"
                onClick={toggleTheme}
                className={`w-full justify-start ${isDark ? 'text-gray-300 hover:text-white hover:bg-slate-800' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} flex items-center gap-2`}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {isDark ? 'Açık Tema' : 'Koyu Tema'}
              </Button>

              {menuItems.map((item, index) => (
                <div key={index}>
                  {item.path ? (
                    <Link
                      to={item.path}
                      onClick={() => {
                        setIsMenuOpen(false);
                        scrollToTop();
                      }}
                    >
                      <Button
                        variant="ghost"
                        className={`h-12 w-full justify-start ${isDark ? 'text-gray-300 hover:text-white hover:bg-slate-800' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} flex items-center gap-2`}
                      >
                        <item.icon className="w-5 h-5" />
                        {item.name}
                        {item.badge && (
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-auto">
                            {item.badge}
                          </span>
                        )}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      variant="ghost"
                      className={`h-12 w-full justify-start ${isDark ? 'text-gray-300 hover:text-white hover:bg-slate-800' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'} flex items-center gap-2`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                      {item.badge && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-auto">
                          {item.badge}
                        </span>
                      )}
                    </Button>
                  )}
                  {item.hasSubmenu && (
                    <div className="ml-4 mt-2 space-y-1">
                      {item.submenu.map((subItem, subIndex) => (
                        subItem.external ? (
                          <a
                            key={subIndex}
                            href={subItem.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              setIsMenuOpen(false);
                            }}
                            className={`block px-4 py-2 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                          >
                            {subItem.name}
                          </a>
                        ) : (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            onClick={() => {
                              setIsMenuOpen(false);
                              scrollToTop();
                            }}
                            className={`block px-4 py-2 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                          >
                            {subItem.name}
                          </Link>
                        )
                      ))}
                      {/* Add "Tümünü Gör" button for Oyun Sunucuları in mobile menu */}
                      {item.name === 'Oyun Sunucuları' && (
                        <div className={`border-t ${isDark ? 'border-slate-700' : 'border-gray-200'} mt-2 pt-2`}>
                          <Link
                            to="/oyunlar"
                            onClick={() => {
                              setIsMenuOpen(false);
                              scrollToTop();
                            }}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${isDark
                              ? 'bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 text-blue-300 hover:text-white hover:from-blue-600/30 hover:to-purple-600/30 hover:border-blue-400/50 hover:shadow-lg hover:shadow-blue-500/20'
                              : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 text-blue-600 hover:text-blue-700 hover:from-blue-100 hover:to-purple-100 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-200/50'
                              }`}
                          >
                            <span>{getContent('navbar.games.all.name', 'Tümünü Gör')}</span>
                            <div className="w-4 h-4 flex items-center justify-center">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <div className={`border-t ${isDark ? 'border-slate-800' : 'border-gray-200'} pt-2 mt-2`}>
                {isLoggedIn ? (
                  <Button
                    onClick={() => {
                      navigate('/dashboard');
                      setIsMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  >
                    Hesabım
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      window.location.href = 'https://musteri.atahost.com.tr/hesap-olustur';
                      setIsMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Müşteri Paneli
                  </Button>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
});

export default Header;

